import React, { useState, useEffect } from 'react';
import { isElectron, isMac, windowControls } from '../utils/platform';
import { WindowControls } from './WindowControls';
import { Smartphone, LogOut, ShieldOff } from 'lucide-react';

interface ResidentBlockedScreenProps {
  onLogout: () => void;
}

const ResidentBlockedScreen: React.FC<ResidentBlockedScreenProps> = ({ onLogout }) => {
  const letters = ['A', 'C', 'C', 'E', 'S', 'S'];
  const [isMaximized, setIsMaximized] = useState(false);

  useEffect(() => {
    if (!isElectron()) return;
    windowControls.isMaximized().then(setIsMaximized);
    const cleanup = windowControls.onStateChanged(async () => {
      setIsMaximized(await windowControls.isMaximized());
    });
    return cleanup;
  }, []);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {isElectron() && (
        <div className="fixed top-0 left-0 right-0 h-8 z-50 flex items-center justify-end electron-drag">
          {!isMac() && (
            <div className="electron-no-drag">
              <WindowControls isMaximized={isMaximized} />
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col items-center text-center px-8">
        <div className="mb-4">
          {/* Shield-centred variant — see LoadingScreen for why not panther-logo.svg. */}
          <img
            src="/panther-logo-mark-centered-white.svg"
            alt="Panther Logo"
            className="w-20 h-20 object-contain opacity-75"
          />
        </div>
        <h2
          className="text-3xl font-bold text-white mb-8"
          style={{ fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.12em' }}
          aria-label="ACCESS"
        >
          {letters.map((letter, index) => (
            <span key={index} className="inline-block">{letter}</span>
          ))}
        </h2>

        <div
          className="mb-8 px-6 py-5 rounded-xl flex flex-col items-center gap-3 max-w-sm"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            minWidth: 280,
          }}
        >
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}
          >
            <ShieldOff size={18} className="text-red-400" />
          </div>
          <div>
            <p className="text-white text-base font-semibold mb-1">Acesso não disponível</p>
            <p className="text-gray-400 text-sm">
              Moradores não têm acesso ao aplicativo de computador. Use o aplicativo móvel Panther Access no seu celular.
            </p>
          </div>
          <div className="flex items-center gap-1.5 mt-1 text-gray-500">
            <Smartphone size={14} />
            <span className="text-xs">Disponível para Android e iOS</span>
          </div>
        </div>

        <button
          onClick={onLogout}
          className="group flex items-center gap-2.5 px-7 py-3 rounded-lg font-semibold text-sm text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 bg-gray-700 hover:bg-gray-600"
        >
          <LogOut size={16} />
          Sair
        </button>
      </div>
    </div>
  );
};

export default ResidentBlockedScreen;
